import { Box, Button, DialogActions, Modal, Stack, TextField, Typography } from '@mui/material'
import type React from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import {
  apiKeySchema,
  deviceLoginIdSchema,
  devicePasswordSchema,
  serialSchema,
} from '../schema/deviceSchema'
import { salesCompanySchema } from '../schema/organizationSchema'
import { LanguageContext } from '../util/LanguageContext'

type ModalDeviceAddDialogProps = {
  open: boolean
  title: string
  onClose: () => void
  onSave: (
    salesCompany: string,
    loginId: string,
    password: string,
    apiKey: string,
    serial: string,
  ) => void
}

export const ModalDeviceAddDialog: React.FC<ModalDeviceAddDialogProps> = props => {
  const { open, onClose, onSave } = props
  const [isOpen, setIsOpen] = useState(open)
  const [salesCompany, setSalesCompany] = useState('')
  const [loginId, setLoginId] = useState('')
  const [password, setPassword] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [serial, setSerial] = useState('')
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [errorSalesCompany, setErrorSalesCompany] = useState('')
  const [errorLoginId, setErrorLoginId] = useState('')
  const [errorPassword, setErrorPassword] = useState('')
  const [errorApiKey, setErrorApiKey] = useState('')
  const [errorSerial, setErrorSerial] = useState('')

  // 文字列変換用
  const translation = useContext(LanguageContext)

  const validateSalesCompany = async (value: string) => {
    try {
      await salesCompanySchema.validate(value)
      setErrorSalesCompany('')
    } catch (err: any) {
      setErrorSalesCompany(err.message)
      setIsButtonDisabled(true)
    }
  }

  const validateLoginId = async (value: string) => {
    try {
      await deviceLoginIdSchema.validate(value)
      setErrorLoginId('')
      // if (serial !== "") {
      //   if (serial === value) {
      //     setErrorLoginId(errorMsg.ID_SERIAL_SAME_ERROR);
      //   } else {
      //     if (errorSerial === errorMsg.ID_SERIAL_SAME_ERROR) {
      //       setErrorSerial("");
      //     }
      //     setErrorLoginId("");
      //   }
      // } else {
      //   setErrorLoginId("");
      // }
    } catch (err: any) {
      setErrorLoginId(err.message)
    }
  }
  const validatePassword = async (value: string) => {
    try {
      await devicePasswordSchema.validate(value)
      setErrorPassword('')
    } catch (err: any) {
      setErrorPassword(err.message)
    }
  }
  const validateApiKey = async (value: string) => {
    try {
      await apiKeySchema.validate(value)
      setErrorApiKey('')
    } catch (err: any) {
      setErrorApiKey(err.message)
    }
  }
  const validateSerial = async (value: string) => {
    try {
      await serialSchema.validate(value)
      // console.log(`loginId:${loginId} serial:${serial}`);
      setErrorSerial('')
      // if (loginId === value) {
      //   setErrorSerial(errorMsg.ID_SERIAL_SAME_ERROR);
      // } else {
      //   if (errorLoginId === errorMsg.ID_SERIAL_SAME_ERROR) {
      //     setErrorLoginId("");
      //   }
      //   setErrorSerial("");
      // }
    } catch (err: any) {
      setErrorSerial(err.message)
    }
  }

  const handleSalesCompanyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSalesCompany(event.target.value)
    validateSalesCompany(event.target.value)
  }
  const handleInputLoginIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoginId(event.target.value)
    validateLoginId(event.target.value)
  }
  const handleInputPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
    validatePassword(event.target.value)
  }
  const handleInputApiChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiKey(event.target.value)
    validateApiKey(event.target.value)
  }
  const handleInputSerialChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSerial(event.target.value)
    validateSerial(event.target.value)
  }

  const handleSave = () => {
    // console.log("handleSave");
    onSave(salesCompany, loginId, password, apiKey, serial)

    onClose()
    setIsOpen(false)
  }
  const handleClose = (event: React.MouseEvent<HTMLInputElement>, reason: 'backdropClick') => {
    if (reason === 'backdropClick') return
    onClose()
    setIsOpen(false)
  }

  // 起動時の処理
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  // 入力チェック
  useEffect(() => {
    if (
      salesCompany !== '' &&
      errorSalesCompany === '' &&
      loginId !== '' &&
      errorLoginId === '' &&
      password !== '' &&
      errorPassword === '' &&
      apiKey !== '' &&
      errorApiKey === '' &&
      serial !== '' &&
      errorSerial === '' &&
      loginId !== serial
    ) {
      // console.log("OK");
      setIsButtonDisabled(false)
    } else {
      // console.log("NG");
      setIsButtonDisabled(true)
    }
  }, [
    salesCompany,
    loginId,
    password,
    apiKey,
    serial,
    errorLoginId,
    errorPassword,
    errorApiKey,
    errorSerial,
  ])

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '550px',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '787px',
          bgcolor: 'background.paper',
          borderRadius: '10px',
          boxShadow: '0px 2px 2px #00000014',
        }}
      >
        <Stack direction="column" justifyContent="center" alignItems="center">
          {/* ダイアログタイトル: 新規追加 */}
          <Typography
            sx={{
              mt: '48px',
              font: 'normal normal bold 32px/48px BIZ UDGothic',
              textAlign: 'center',
            }}
          >
            {translation['button_add_new']}
          </Typography>

          {/* Sales company */}
          <Typography
            sx={{
              left: '190px',
              width: '402px',
              mt: '50px',
              color: '#282828',
              font: 'normal normal normal 21px/32px Noto Sans JP',
              textAlign: 'left',
            }}
          >
            {translation['label_SalesCompany_info']}
          </Typography>
          <TextField
            value={salesCompany}
            error={!!errorSalesCompany}
            helperText={errorSalesCompany ? translation[errorSalesCompany] : ' '}
            onChange={handleSalesCompanyChange}
            InputProps={{
              sx: {
                font: 'normal normal normal 21px/29px Noto Sans JP',
                borderRadius: '10px',
                height: '53px',
                pl: '8px',
                background: '#F4F4F4',
              },
            }}
            sx={{
              width: '402px',
              '& .MuiFormHelperText-root': {
                whiteSpace: 'nowrap',
              },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: errorSalesCompany ? 'red' : '#282828',
                },
              },
            }}
          />

          {/* ID */}
          <Typography
            sx={{
              //position: "absolute",
              top: '118px',
              left: '190px',
              width: '402px',
              //mt: "18px",
              color: '#282828',
              font: 'normal normal normal 21px/32px Noto Sans JP',
              textAlign: 'left',
            }}
          >
            {translation['label_id']}
          </Typography>
          <TextField
            label=""
            value={loginId}
            error={!!errorLoginId}
            helperText={errorLoginId ? translation[errorLoginId] : ' '}
            inputRef={inputRef}
            onChange={handleInputLoginIdChange}
            InputProps={{
              sx: {
                font: 'normal normal normal 21px/29px Noto Sans JP',
                borderRadius: '10px',
                height: '53px',
                pl: '8px',
                background: '#F4F4F4',
              },
            }}
            sx={{
              width: '402px',
              //mt: "74px",

              // "& .MuiFormHelperText-root": {
              //   whiteSpace: "nowrap",
              // },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: errorLoginId ? 'red' : '#282828',
                },
              },
            }}
          />

          {/* プロジェクタシリアル番号 */}
          <Typography
            sx={{
              //position: "absolute",
              //top: "263px",
              //left: "190px",
              left: '220px',
              width: '440px',
              ml: '30px',
              // mt: "18px",
              color: '#282828',
              font: 'normal normal normal 21px/32px Noto Sans JP',
              textAlign: 'left',
            }}
          >
            {translation['projector_serial_number']}
          </Typography>
          <TextField
            label=""
            value={password}
            error={!!errorPassword}
            helperText={errorPassword ? translation[errorPassword] : ' '}
            inputRef={inputRef}
            onChange={handleInputPasswordChange}
            InputProps={{
              sx: {
                font: 'normal normal normal 21px/29px Noto Sans JP',
                background: '#F4F4F4',
                borderRadius: '10px',
                height: '53px',
                pl: '8px',
              },
            }}
            sx={{
              width: '402px',
              //pl: "8px",
              //mt: "54px",

              '& .MuiFormHelperText-root': {
                whiteSpace: 'nowrap',
              },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: errorPassword ? 'red' : '#282828',
                },
              },
            }}
          />

          {/* APIキー */}
          <Typography
            sx={{
              //position: "absolute",
              //top: "378px",
              left: '190px',
              width: '402px',
              //mt: "18px",
              color: '#282828',
              font: 'normal normal normal 21px/32px Noto Sans JP',
              textAlign: 'left',
            }}
          >
            {translation['label_api_key']}
          </Typography>
          <TextField
            label=""
            value={apiKey}
            error={!!errorApiKey}
            helperText={errorApiKey ? translation[errorApiKey] : ' '}
            inputRef={inputRef}
            onChange={handleInputApiChange}
            FormHelperTextProps={{
              style: { whiteSpace: 'normal' }, // ここで改行を許可します
            }}
            InputProps={{
              sx: {
                font: 'normal normal normal 21px/29px Noto Sans JP',
                borderRadius: '10px',
                height: '53px',
                // width: "402px",
                pl: '8px',
                background: '#F4F4F4',
              },
            }}
            sx={{
              width: '402px',
              //mt: "54px",

              // "& .MuiFormHelperText-root": {
              //   whiteSpace: "nowrap",
              // },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: errorApiKey ? 'red' : '#282828',
                },
              },
            }}
          />

          {/* アプリ初期設定デバイス番号 */}
          <Typography
            sx={{
              //position: "absolute",
              //top: "533px",
              left: '190px',
              width: '402px',
              // mt: "18px",
              color: '#282828',
              font: 'normal normal normal 21px/32px Noto Sans JP',
              textAlign: 'left',
            }}
          >
            {translation['label_initial_setting_device_number']}
          </Typography>
          <TextField
            label=""
            value={serial}
            error={!!errorSerial}
            helperText={errorSerial ? translation[errorSerial] : ' '}
            inputRef={inputRef}
            onChange={handleInputSerialChange}
            FormHelperTextProps={{
              style: { whiteSpace: 'normal' }, // ここで改行を許可します
            }}
            InputProps={{
              sx: {
                font: 'normal normal normal 21px/29px Noto Sans JP',
                borderRadius: '10px',
                height: '53px',
                pl: '8px',
                background: '#F4F4F4',
              },
            }}
            sx={{
              width: '402px',
              //mt: "64px",
              // "& .MuiFormHelperText-root": {
              //   whiteSpace: "normal",
              // },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: errorSerial ? 'red' : '#282828',
                },
              },
            }}
          />

          <DialogActions sx={{ mt: '30px', mb: '60px' }}>
            <Button
              onClick={onClose}
              sx={{
                height: '49px',
                width: '181px',
                font: 'normal normal bold 21px/29px Noto Sans JP',
                color: '#282828',
                background: '#FFFFFF 0% 0% no-repeat padding-box',
                border: '2px solid #282828',
                borderRadius: '10px',
                mt: '5px',
                mr: '30px',
                '&:hover': {
                  background: '#FFFFFF',
                  opacity: 0.8,
                },
              }}
            >
              {translation['button_cancel']}
            </Button>
            <Button
              type="submit"
              disabled={isButtonDisabled}
              onClick={handleSave}
              sx={{
                height: '49px',
                width: '181px',
                font: 'normal normal bold 21px/29px Noto Sans JP',
                color: isButtonDisabled ? '#CACACA' : '#FFFFFF',
                background: isButtonDisabled ? '#F4F4F4 0% 0% no-repeat padding-box' : '#282828',
                borderRadius: '10px',
                mt: '5px',
                '&:hover': {
                  backgroundColor: '#282828',
                  opacity: 0.8,
                  boxShadow: 0,
                },
              }}
            >
              {translation['button_add']}
            </Button>
          </DialogActions>
        </Stack>
      </Box>
    </Modal>
  )
}
